/**
 * Terminal success panel — celebrates the enrollment and routes the admin
 * to the post-enrollment configuration steps the API intentionally leaves out.
 */

import {
  Sparkles,
  ExternalLink,
  Sliders,
  Boxes,
  Users,
  ArrowRight
} from "lucide-react"
import { EnrollmentResponse } from "../types"

interface Props {
  enrollment: EnrollmentResponse
  companyName?: string
  onEnrollAnother: () => void
}

const NextStep = ({
  index,
  icon,
  title,
  description,
  href
}: {
  index: number
  icon: React.ReactNode
  title: string
  description: string
  href: string
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-4 transition-all hover:border-primary-300 hover:shadow-sm"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-100 transition-colors">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
          Next {String(index).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-sm font-medium text-neutral-900">
        {title}
        <ExternalLink
          size={12}
          className="text-neutral-300 group-hover:text-primary-500 transition-colors"
        />
      </div>
      <p className="text-xs text-neutral-500 mt-0.5">{description}</p>
    </div>
    <ArrowRight
      size={16}
      className="mt-3 shrink-0 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500"
    />
  </a>
)

const StatPill = ({
  label,
  value,
  href
}: {
  label: string
  value: number | string
  href?: string
}) => {
  const content = (
    <>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-mono text-neutral-900">{value}</div>
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        className="block rounded-lg border border-neutral-200 bg-white px-3 py-2 hover:border-primary-300 hover:shadow-sm transition-all"
      >
        {content}
      </a>
    )
  }
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
      {content}
    </div>
  )
}

const EnrollmentSuccess = ({
  enrollment,
  companyName,
  onEnrollAnother
}: Props) => {
  const brandId = enrollment.brand_id ?? 0
  const orgId = enrollment.org_id ?? 0
  const loid = enrollment.loid ?? 0
  const rateflowCreated = enrollment.steps.rateflow?.status === "success"

  return (
    <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
      {/* Hero */}
      <header className="relative px-6 py-7 bg-gradient-to-br from-emerald-50 to-white border-b border-emerald-100">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.15),transparent_60%)]" />
        <div className="relative flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-sm">
            <Sparkles size={20} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
              Enrollment complete
            </div>
            <h2 className="mt-1 font-serif text-2xl text-neutral-900 leading-tight">
              {companyName || "Customer"} is live
            </h2>
            <p className="mt-2 text-sm text-neutral-600 max-w-md">
              {enrollment.message}
            </p>
          </div>
        </div>

        <div className="relative mt-5 grid grid-cols-3 gap-2">
          <StatPill
            label="Org"
            value={`#${orgId}`}
            href={`/organizations/${orgId}`}
          />
          <StatPill
            label="Brand"
            value={`#${brandId}`}
            href={`/brands/${brandId}`}
          />
          <StatPill
            label="First LO"
            value={`#${loid}`}
            href={`/loan-officers/${loid}`}
          />
        </div>
      </header>

      {/* Next steps */}
      <div className="px-6 py-5">
        <h3 className="font-serif text-base text-neutral-900 mb-3">
          What to do next
        </h3>
        <div className="space-y-2">
          {rateflowCreated && (
            <NextStep
              index={1}
              icon={<Sliders size={18} />}
              title="Configure rateflow credentials"
              description="The api_mode is set, but customer_id, email, and api_keys still need to be entered."
              href={`/pricing-engine/${enrollment.steps.rateflow?.id ?? ""}`}
            />
          )}
          <NextStep
            index={rateflowCreated ? 2 : 1}
            icon={<Boxes size={18} />}
            title="Tweak embed settings"
            description="UTM tracking, rate-card config, and per-embed customization."
            href={`/integrations`}
          />
          <NextStep
            index={rateflowCreated ? 3 : 2}
            icon={<Users size={18} />}
            title="Invite more loan officers"
            description="Enrollment creates exactly one admin LO. Add the rest of the team."
            href={`/loan-officers`}
          />
        </div>

        <div className="mt-5 pt-5 border-t border-neutral-100 flex items-center justify-between">
          <div className="text-xs text-neutral-400 font-mono">
            Enrollment #{enrollment.enrollment_id}
          </div>
          <button
            type="button"
            onClick={onEnrollAnother}
            className="text-sm text-primary-500 hover:text-primary-600 font-medium"
          >
            Enroll another customer →
          </button>
        </div>
      </div>
    </div>
  )
}

export default EnrollmentSuccess
